import React, { useState, useEffect, useCallback } from "react";
import { User } from "./UserCard";
import { Card, Col, Nav, Row, Table, Button, Modal, Form } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import SectionHeader from "./HeaderSection";
import { WhiteBoxWrapper } from "./Login.style";
import { FaArrowLeft, FaTrash, FaEdit } from "react-icons/fa";
import DeviceCard, { Device } from "./DeviceCard";
import { fetchAPI } from "../utils/fetchAPI";

const UserDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState<User>();
  const [assignedDevices, setAssignedDevices] = useState<Device[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    surname: "",
    position: "",
    avatar: null as File | null,
  });

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const data = await fetchAPI("GET", `user.php?id=${id}`);

      if (data.success) {
        setUser(data.data);
        setAssignedDevices(data.assignedDevices || []);
        setHistory(data.history || []);
        setEditForm({
          name: data.data.name,
          surname: data.data.surname,
          position: data.data.position,
          avatar: null,
        });
      } else {
        throw new Error(data.message || "Nie udało się pobrać użytkownika.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas pobierania danych.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const summarizeDevices = () => {
    const summary: { [key: string]: number } = {};
    history.forEach((log) => {
      if (log.device && log.action === 0) {
        summary[log.device.name] = (summary[log.device.name] || 0) + log.countOut;
      }
    });
    return summary;
  };

  const handleDeleteUser = async () => {
    try {
      setIsLoading(true);
      const response = await fetchAPI("DELETE", `deleteUser.php?id=${id}`);

      if (response.success) {
        alert("Użytkownik został usunięty.");
        navigate("/users");
      } else {
        throw new Error(response.message || "Nie udało się usunąć użytkownika.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas usuwania użytkownika.");
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleEditUser = async () => {
    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append("id", user?.id?.toString() || "");
      formData.append("name", editForm.name);
      formData.append("surname", editForm.surname);
      formData.append("position", editForm.position);
      if (editForm.avatar) {
        formData.append("avatar", editForm.avatar);
      }

      const response = await fetchAPI("POST", "updateUser.php", formData);

      if (response.success) {
        setShowEditModal(false);
        fetchUsers(); // Odśwież dane użytkownika
      } else {
        throw new Error(response.message || "Nie udało się zaktualizować użytkownika.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas aktualizacji użytkownika.");
    } finally {
      setIsLoading(false);
    }
  };

  const deviceSummary = summarizeDevices();

  return (
    <div>
      <WhiteBoxWrapper>
        <Row className="align-items-center">
          <Col xs={2}>
            <h2>
              <Nav.Link as={Link} to="/users">
                <FaArrowLeft size={24} />
              </Nav.Link>
            </h2>
          </Col>
          <Col xs={8}>
            <h2 className="text-center">
              <b>
                {user?.name} {user?.surname} #{id}
              </b>
            </h2>
          </Col>
          <Col xs={2} className="text-end">
            <Button variant="outline-primary" onClick={() => setShowEditModal(true)}>
              <FaEdit /> Edytuj
            </Button>
          </Col>
        </Row>
      </WhiteBoxWrapper>

      <Card className="p-2 bg-brand">
        <div className="text-center mb-3">
          <img
            src={user?.avatar ? "/backend/" + user.avatar : "./assets/img/user.jpg"}
            alt={`${user?.name} ${user?.surname}`}
            style={{
              maxWidth: "150px",
              width: "100%",
              height: "auto",
              objectFit: "cover",
              borderRadius: "8px",
            }}
          />
        </div>
        <SectionHeader>Stanowisko: {user?.position}</SectionHeader>
        <SectionHeader>Przydzielone wyposażenie</SectionHeader>
        {assignedDevices.length > 0 ? (
          assignedDevices.map((device) => <DeviceCard key={device.id} data={device} />)
        ) : (
          <p className="text-center">Brak przydzielonego wyposażenia.</p>
        )}
      </Card>

      <Card className="p-2 bg-brand mt-4">
        <SectionHeader>Podsumowanie wydań</SectionHeader>
        <div className="mt-2">
          {Object.keys(deviceSummary).length > 0 && (
            <div>
              {Object.entries(deviceSummary).map(([deviceName, count]) => (
                <p key={deviceName}>
                  {deviceName}: {count} sztuk
                </p>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card className="p-2 bg-brand mt-4">
        <SectionHeader>Pełna Historia</SectionHeader>
        {history.length > 0 ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Akcja</th>
                <th>Urządzenie</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {history.map((log, index) => (
                <tr key={index}>
                  <td>{log.Info}</td>
                  <td>
                    {log.device ? (
                      <span onClick={() => navigate(`/device/${log.device?.id}`)}>
                        <b>
                          {log.device.name} - {log.device.description}
                        </b>
                      </span>
                    ) : (
                      "Brak danych o urządzeniu"
                    )}
                  </td>
                  <td>{new Date(log.datelog).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>Brak historii do wyświetlenia.</p>
        )}

        {isLoading && <p>Ładowanie danych...</p>}
        {errorMessage && <p className="error">{errorMessage}</p>}

        {!isLoading && !errorMessage && !user && <p>Brak danych do wyświetlenia.</p>}
      </Card>
      <Button variant="danger w-100 mt-3" onClick={() => setShowDeleteModal(true)}>
        <FaTrash /> Usuń Pracownika
      </Button>
      {/* Modal potwierdzenia usunięcia */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Potwierdzenie usunięcia</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Czy na pewno chcesz usunąć użytkownika{" "}
          <b>
            {user?.name} {user?.surname}
          </b>
          ? Tej operacji nie można cofnąć!
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Anuluj
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            Usuń
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal edycji użytkownika */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edycja użytkownika</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Imię</Form.Label>
              <Form.Control
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nazwisko</Form.Label>
              <Form.Control
                type="text"
                value={editForm.surname}
                onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Stanowisko</Form.Label>
              <Form.Control
                type="text"
                value={editForm.position}
                onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Zdjęcie profilowe</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.files && e.target.files[0]) {
                    setEditForm({ ...editForm, avatar: e.target.files[0] });
                  }
                }}
              />
              {editForm.avatar && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(editForm.avatar)}
                    alt="Podgląd"
                    style={{ maxWidth: "100px", maxHeight: "100px" }}
                  />
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleEditUser} disabled={isLoading}>
            {isLoading ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserDetails;
